import { type Context, Controller } from '@hotwired/stimulus';
import * as d3 from 'd3';

type DataNetworkNode = {
  id: string;
  name: string;
  href: string;
  author: null | { id: string; href: string; name: string };
};

type SimulationNodeData = DataNetworkNode & d3.SimulationNodeDatum;
type SimulationLinkData = {
  source: { id: string } & SimulationNodeData;
  target: { id: string } & SimulationNodeData;
} & d3.SimulationLinkDatum<SimulationNodeData>;

const NODE_RADIUS = 12;
const LINK_DISTANCE = 20;
const ARROW_LENGTH = 8;

export default class GraphNetworkController extends Controller {
  static targets = ['table'];

  public static values = {
    data: Object,
    width: Number,
    height: Number,
    identifier: String,
  };

  private declare tableTarget: HTMLTableElement;

  private declare readonly dataValue: Readonly<{
    roots: readonly string[];
    focus: string;
    graph: readonly ReadonlyArray<string>[];
    nodes: Readonly<Record<string, DataNetworkNode>>;
    path?: string[];
  }>;
  private declare readonly widthValue: number;
  private declare readonly heightValue: number;
  private declare readonly identifierValue: string;

  private onResize: (() => void) | undefined;

  public constructor(context: Context) {
    super(context);
  }

  public connect(): void {
    // Declare the chart dimensions and margins.
    const width = this.widthValue;
    const height = this.heightValue;

    const nodes = Object.values(this.dataValue.nodes).map((node, index) => {
      return { ...node, index } satisfies SimulationNodeData;
    });

    // No data
    if (nodes.length === 0) {
      return;
    }

    const links = this.dataValue.graph.reduce<
      {
        source: { id: string } & SimulationNodeData;
        target: { id: string } & SimulationNodeData;
      }[]
    >((result, path) => {
      path.forEach((item, index, self) => {
        if (index === 0) {
          return;
        }

        // D3 will augment the links with the node data, so casting here is
        // relatively safe.
        result.push({
          source: self[index - 1] as unknown as SimulationNodeData,
          target: item as unknown as SimulationNodeData,
        });
      });

      return result;
    }, []);

    // Create the SVG container.
    const canvas = d3
      .create('canvas')
      .classed('--interactive', true)
      .style('-webkit-tap-highlight-color', 'transparent')
      .style('overflow', 'visible')
      .attr('aria-hidden', 'true')
      .attr('width', width)
      .attr('height', height)
      .style('border', '1px dotted white')
      .classed('max-w-full', true)
      .node()!;

    /* .on('pointerenter pointermove', onPointerMoved)
      .on('pointerleave', onPointerLeft)
      .on('touchstart', (event) => event.preventDefault()); */

    const context = canvas.getContext('2d')!;

    const drawNetwork = (
      context: CanvasRenderingContext2D,
      width: number,
      height: number,
      networkNodes: readonly SimulationNodeData[],
      networkLinks: readonly SimulationLinkData[],
    ) => {
      context.clearRect(0, 0, width, height);

      // Draw the nodes
      context.beginPath();
      networkNodes.forEach((node) => {
        if (node.id === this.dataValue.focus) {
          return;
        }

        if (this.dataValue.roots.includes(node.id)) {
          return;
        }

        if (this.dataValue.path && !this.dataValue.path.includes(node.id)) {
          return;
        }

        this.drawNode(context, node);
      });
      context.fillStyle = '#cb1dd1';
      context.fill();

      // Mute all the nodes not in the active path
      if (this.dataValue.path) {
        context.beginPath();
        networkNodes.forEach((node) => {
          if (node.id === this.dataValue.focus) {
            return;
          }

          if (this.dataValue.roots.includes(node.id)) {
            return;
          }

          if (this.dataValue.path && this.dataValue.path.includes(node.id)) {
            return;
          }

          this.drawNode(context, node);
        });
        context.fillStyle = '#555555';
        context.fill();
      }

      // Draw root node(s)
      context.beginPath();
      networkNodes.forEach((node) => {
        if (!this.dataValue.roots.includes(node.id)) {
          return;
        }

        this.drawNode(context, node);
      });
      context.fillStyle = '#0b1dd1';
      context.fill();

      // Draw focus (current) node
      context.beginPath();
      networkNodes.forEach((node) => {
        if (node.id !== this.dataValue.focus) {
          return;
        }

        this.drawNode(context, node);
      });
      context.fillStyle = '#0bFFd1';
      context.fill();

      // Draw the links
      context.beginPath();
      networkLinks.forEach((link) => this.drawLink(context, link));
      context.strokeStyle = 'white';
      context.stroke();
    };

    const simulation = d3
      .forceSimulation(nodes)
      // Force #1: links between nodes
      .force(
        'link',
        d3
          .forceLink<SimulationNodeData, SimulationLinkData>(links)
          .id((d) => d.id),
      )
      // Force #2: avoid node overlaps
      .force(
        'collide',
        d3
          .forceCollide()
          .radius(NODE_RADIUS * 1.5)
          .iterations(4),
      )
      // Force #3: attraction or repulsion between nodes
      .force('charge', d3.forceManyBody().strength(-100))
      // Force #4: nodes are attracted by the center of the chart area
      .force('center', d3.forceCenter(width / 2, height / 2))
      .on('tick', () => {
        drawNetwork(context, width, height, nodes, links);
      })
      .on('end', () => {});

    let zoom = 1;

    this.onResize = () => {
      requestAnimationFrame(
        () => (zoom = 1 / (canvas.getBoundingClientRect().width / width)),
      );
    };

    window.addEventListener('resize', this.onResize);
    this.onResize();

    canvas.addEventListener('pointermove', (event) => {
      const hoveredNode = simulation.find(
        event.offsetX * zoom,
        event.offsetY * zoom,
        NODE_RADIUS,
      );

      canvas.classList.toggle('cursor-pointer', !!hoveredNode);
    });

    canvas.addEventListener('click', (event) => {
      const clickedNode = simulation.find(
        event.offsetX * zoom,
        event.offsetY * zoom,
        NODE_RADIUS,
      );
      if (!clickedNode) {
        return;
      }

      window.location.href = clickedNode.href;
    });

    this.element.append(canvas);
  }

  private drawLink(
    context: CanvasRenderingContext2D,
    link: SimulationLinkData,
  ) {
    if (typeof link.source !== 'object' || typeof link.target !== 'object') {
      return;
    }

    if (link.source.x === undefined || link.source.y === undefined) {
      return;
    }

    if (link.target.x === undefined || link.target.y === undefined) {
      return;
    }

    const headLength = ARROW_LENGTH; // length of head in pixels
    const dx = link.target.x - link.source.x;
    const dy = link.target.y - link.source.y;
    const angle = Math.atan2(dy, dx);

    const targetDx = NODE_RADIUS * Math.cos(angle);
    const targetDy = NODE_RADIUS * Math.sin(angle);

    const sourceX = link.source.x + targetDx;
    const sourceY = link.source.y + targetDy;

    const targetX = link.target.x - targetDx;
    const targetY = link.target.y - targetDy;

    context.moveTo(sourceX, sourceY);
    context.lineTo(targetX, targetY);
    context.lineTo(
      targetX - headLength * Math.cos(angle - Math.PI / 6),
      targetY - headLength * Math.sin(angle - Math.PI / 6),
    );
    context.moveTo(targetX, targetY);
    context.lineTo(
      targetX - headLength * Math.cos(angle + Math.PI / 6),
      targetY - headLength * Math.sin(angle + Math.PI / 6),
    );
  }

  drawNode(context: CanvasRenderingContext2D, node: SimulationNodeData) {
    if (node.x === undefined || node.y === undefined) {
      return;
    }

    context.moveTo(node.x + NODE_RADIUS, node.y);
    context.arc(node.x, node.y, NODE_RADIUS, 0, 2 * Math.PI);
  }

  public disconnect(): void {
    this.element.querySelector('canvas')?.remove();

    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }

    this.onResize = undefined;
  }
}
